import React, { type ComponentProps, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';
import Animated, {
  interpolateColor,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';
import { ContentfulImage } from '../../../../../Image';
import { BodyText } from '../../../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingPageLink = (props: Props) => {
  const { title, description, imageAlt, imageSrc, onPress, ...restProps } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const linkRef = useRef<View>(null);
  const isHovered = useHovered(linkRef);

  // ─── Animated Values ───────────────────────────────────────────────────────

  const hoverSV = useDerivedValue(() => {
    return withTiming(isHovered ? 1 : 0, LINK_TIMING_CONFIG);
  });

  // ─── Animated Styles ───────────────────────────────────────────────────────

  const linkAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        hoverSV.value,
        LINK_HOVER_INPUT_RANGE,
        LINK_BACKGROUND_COLORS,
      ),
    };
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const linkStyles = [styles.link, linkAnimatedStyle];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity accessibilityRole="link" activeOpacity={0.7} onPress={onPress} {...restProps}>
      <Animated.View ref={linkRef} style={linkStyles}>
        <ContentfulImage
          baseUrl={imageSrc}
          contentFit="cover"
          alt={imageAlt}
          options={IMAGE_CONFIG}
          style={styles.image}
        />

        <View style={styles.contentContainer}>
          <BodyText bold maxFontSizeMultiplier={1.2} sizeMatch={['16']} style={styles.title}>
            {title}
          </BodyText>

          {description ? (
            <BodyText maxFontSizeMultiplier={1.2} sizeMatch={['14']} style={styles.description}>
              {description}
            </BodyText>
          ) : null}
        </View>
      </Animated.View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LINK_HOVER_INPUT_RANGE = [0, 1];
const LINK_BACKGROUND_COLORS = [theme.colors.OPACITY.WHITE.TRANSPARENT, theme.colors.WHITE];
const LINK_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.base };

const IMAGE_SIZE = 80;
const IMAGE_CONFIG: ImageOptions = { w: IMAGE_SIZE };

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
    padding: theme.spacing['2'],
    borderRadius: theme.radius.large,
  },
  image: {
    width: IMAGE_SIZE,
    height: IMAGE_SIZE,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.CREAM,
  },
  contentContainer: {
    flex: 1,
    gap: theme.spacing['1'],
  },
  title: {
    color: theme.colors.CHARCOAL,
  },
  description: {
    color: 'rgba(72, 77, 72, 0.6)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  title: string;
  description?: string;
  imageAlt: string;
  imageSrc: string;
} & Pick<TouchableOpacityProps, 'onPress'>;

type ImageOptions = ComponentProps<typeof ContentfulImage>['options'];
